import React, { useEffect, useState, useRef } from 'react';
import { gameState } from '../game/state/GameState';
import { loopController } from '../game/core/LoopController';
import { GAME_STATES, TILE_STATUS } from '../game/constants';
import MysteryHub from './MysteryHub';
import BootcampVault from './BootcampVault';
import GameControls from './GameControls';
import { fetchVault, fetchProgress, claimTile, solveTile, completeBootcampProgress } from '../game/actions/TileActions';

import TileDetailsModal from './TileDetailsModal';
import SquadLobby from './SquadLobby';
import '../styles/app.css';
import { GAME_NAME, IS_LOCAL_MODE, canUseDebugTools } from '../config/game_config';
import { getSquadApiBase } from '../config/game_config';
import {
    getTelegramStartParam,
    parseSyndicateInviteId,
    parsePublicFloorStart,
} from '../config/telegramLaunch';
import { BOOTCAMP_STORAGE_KEY } from '../game/config/bootcampConfig';
import { MYSTERY_TIER, PUBLIC_ATELIER_ID, PUBLIC_WORKSHOP_ID } from '../game/config/tiers';
import { trackEvent } from '../game/meta/analytics';
/**
 * Main Voxel Quest container
 */
const VoxelGame = () => {
    const [currentState, setCurrentState] = useState(gameState.getCurrentState());
    const [lastTick, setLastTick] = useState(Date.now());
    const [feedback, setFeedback] = useState(null);
    const feedbackTimer = useRef(null);
    const [selectedTile, setSelectedTile] = useState(null);
    const [isMinimized, setIsMinimized] = useState(false);
    const [viewMode, setViewMode] = useState('GAME'); // 'GAME' or 'LOBBY'
    const [lobbyInitialView, setLobbyInitialView] = useState('list');

    // 1. Initialize Game on Mount
    useEffect(() => {
        let cancelled = false;
        let userCtx = { id: 'dev_user_local', username: 'local_player' };

        const tg = window.Telegram?.WebApp;
        if (tg?.initDataUnsafe?.user) {
            userCtx = {
                id: String(tg.initDataUnsafe.user.id),
                username: tg.initDataUnsafe.user.username,
            };
            console.log('[VoxelGame] Authenticated as Telegram User:', userCtx.id);
        } else {
            console.warn('[VoxelGame] LOCAL MODE — mock user:', userCtx.id);
        }

        const params = new URLSearchParams(window.location.search);
        const forceBootcamp = params.get('forceBootcamp') === '1';
        const skipBootcampQuery =
            params.get('skipBootcamp') === '1' || import.meta.env.VITE_SKIP_BOOTCAMP === '1';
        const localBootcampDone = localStorage.getItem(BOOTCAMP_STORAGE_KEY) === 'true';

        if (forceBootcamp) {
            localStorage.removeItem(BOOTCAMP_STORAGE_KEY);
            localStorage.removeItem('bootcamp_complete');
            sessionStorage.removeItem('vq_bootcamp_intro_done');
            userCtx.hasCompletedBootcamp = false;
        }

        const postBootcamp = sessionStorage.getItem('vq_post_bootcamp');
        if (postBootcamp) {
            sessionStorage.removeItem('vq_post_bootcamp');
            if (postBootcamp === 'public') {
                gameState.setMysteryTier('workshop');
                gameState.setSquad('public-workshop-0000', 'Public Workshop');
            } else if (postBootcamp === 'create' || postBootcamp === 'join') {
                setLobbyInitialView(postBootcamp);
                setViewMode('LOBBY');
            } else {
                setLobbyInitialView('list');
                setViewMode('LOBBY');
            }
        }

        const authHeaders = () => {
            const headers = { 'Content-Type': 'application/json' };
            if (tg?.initData) headers.Authorization = `tma ${tg.initData}`;
            else headers.Authorization = 'mock dev_user_local';
            return headers;
        };

        const joinFromDeepLink = async () => {
            const start = getTelegramStartParam();
            const publicFloor = parsePublicFloorStart(start);
            if (publicFloor) {
                if (publicFloor.kind === 'atelier') {
                    if (gameState.atelierUnlocked) {
                        gameState.setMysteryTier(MYSTERY_TIER.ATELIER);
                        gameState.setSquad(PUBLIC_ATELIER_ID, 'Public Atelier');
                    } else {
                        gameState.setMysteryTier(MYSTERY_TIER.WORKSHOP);
                        gameState.setSquad(PUBLIC_WORKSHOP_ID, 'Public Workshop');
                        setFeedback({
                            type: 'info',
                            title: 'Atelier locked',
                            body: 'Opened Public Workshop — unlock Atelier by solving Workshop slices.',
                        });
                    }
                } else {
                    gameState.setMysteryTier(MYSTERY_TIER.WORKSHOP);
                    gameState.setSquad(PUBLIC_WORKSHOP_ID, 'Public Workshop');
                }
                trackEvent('deep_link_public', { kind: publicFloor.kind });
                return true;
            }

            const inviteId = parseSyndicateInviteId(start);
            if (!inviteId) return false;
            try {
                const res = await fetch(`${getSquadApiBase()}/join`, {
                    method: 'POST',
                    headers: authHeaders(),
                    body: JSON.stringify({ syndicateId: inviteId }),
                });
                if (!res.ok) {
                    const text = await res.text();
                    console.warn('[DeepLink] join failed', text);
                    setFeedback({
                        type: 'error',
                        title: 'Invite join failed',
                        body: text || 'Could not join syndicate from link',
                    });
                    return false;
                }
                const data = await res.json();
                const id = data.data?.id || inviteId;
                const name = data.data?.name || 'Syndicate';
                gameState.setSquad(id, name);
                trackEvent('deep_link_syndicate', { joined: true });
                setFeedback({
                    type: 'success',
                    title: 'Joined syndicate',
                    body: name,
                });
                return true;
            } catch (e) {
                console.warn('[DeepLink]', e);
                return false;
            }
        };

        (async () => {
            // Resolve bootcamp from server before init so phone ↔ desktop share graduation.
            if (!forceBootcamp) {
                const prog = await fetchProgress();
                if (cancelled) return;
                const serverDone = !!prog?.data?.bootcampCompleted;
                if (serverDone) {
                    userCtx.hasCompletedBootcamp = true;
                    localStorage.setItem(BOOTCAMP_STORAGE_KEY, 'true');
                } else if (localBootcampDone || skipBootcampQuery) {
                    // Local graduate (or skip) → push to server for other clients
                    userCtx.hasCompletedBootcamp = true;
                    localStorage.setItem(BOOTCAMP_STORAGE_KEY, 'true');
                    await completeBootcampProgress();
                } else {
                    userCtx.hasCompletedBootcamp = false;
                }
            }

            if (cancelled) return;
            loopController.initGame(userCtx);

            if (IS_LOCAL_MODE) {
                window.__vqLocal = { gameState, loopController, claimTile, solveTile, TILE_STATUS };
            }

            await joinFromDeepLink();
            if (cancelled) return;
            // Refresh progress after deep-link may have changed squad (atelier unlock already applied)
            await fetchProgress();
            if (forceBootcamp) {
                // Keep replay session — don't let server flag re-arm local graduate cache mid-tutorial
                localStorage.removeItem(BOOTCAMP_STORAGE_KEY);
                localStorage.removeItem('bootcamp_complete');
            }
            if (cancelled) return;
            const res = await fetchVault();
            if (cancelled) return;
            console.log('[VoxelGame] Initial Mystery Scan Complete', res);
            if (!res || !res.success) {
                setFeedback({ type: 'error', title: 'FAILED TO LOAD MYSTERY DATA' });
            }
            setCurrentState(gameState.getCurrentState());
        })().catch((err) => {
            console.error('[VoxelGame] Mystery Fetch Failed:', err);
            // Offline fallback: use local bootcamp flag only
            if (!forceBootcamp && !userCtx.hasCompletedBootcamp) {
                userCtx.hasCompletedBootcamp =
                    localBootcampDone || skipBootcampQuery || false;
            }
            if (!gameState.currentUser) {
                loopController.initGame(userCtx);
            }
            setFeedback({ type: 'error', title: 'CONNECTION ERROR', body: 'Retrying…' });
            setCurrentState(gameState.getCurrentState());
        });

        const unsubscribe = gameState.subscribe((newState, stateInstance) => {
            setCurrentState(newState);
            if (stateInstance.selectedTileId) {
                const t = stateInstance.vault.getTile(stateInstance.selectedTileId);
                setSelectedTile(t ? { ...t } : null);
            } else {
                setSelectedTile(null);
            }
        });

        return () => {
            cancelled = true;
            unsubscribe();
        };
    }, []);

    // Shared progress: poll mystery vault so peers see claims/solves without reload
    useEffect(() => {
        if (viewMode !== 'GAME') return undefined;
        const tick = () => {
            if (gameState.isBootcampMode) return;
            if (document.visibilityState === 'hidden') return;
            fetchVault().catch(() => {});
        };
        const id = setInterval(tick, 8000);
        const onVis = () => {
            if (document.visibilityState === 'visible') tick();
        };
        document.addEventListener('visibilitychange', onVis);
        return () => {
            clearInterval(id);
            document.removeEventListener('visibilitychange', onVis);
        };
    }, [viewMode]);

    // 2. Game Loop Effect
    useEffect(() => {
        const intervalId = setInterval(() => {
            const now = Date.now();
            const releasedIds = loopController.updateLoop(now) || [];
            if (releasedIds.length > 0) {
                handleFeedback({
                    type: 'error',
                    title: 'Claim timed out',
                    body: 'Slice reopened for others. Draft kept — reclaim with a key to resume.',
                });
            }
            setCurrentState(gameState.getCurrentState());

            // Sync Selection
            const selId = gameState.selectedTileId;

            // Auto-Select Logic (Unless Minimized)
            if (currentState === 'PUZZLE_ACTIVE' && !selId && !isMinimized) {
                const ownerId = gameState.currentUser?.id;
                const lockedTile = gameState.vault.findActiveLockForUser(ownerId);
                if (lockedTile) {
                    // Auto-select
                    gameState.selectTile(lockedTile.id);
                }
            }

            if (selId) {
                const tile = gameState.vault.getTile(selId);
                setSelectedTile(tile ? { ...tile } : null);
                // If we have a selection, we are not minimized
                if (selId && isMinimized) setIsMinimized(false);
            } else {
                setSelectedTile(null);
            }

            setLastTick(now);
        }, 100);

        return () => clearInterval(intervalId);
    }, [isMinimized, currentState]); // Added Dependencies

    const handleFeedback = (msg) => {
        if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
        const toast =
            msg && typeof msg === 'object'
                ? msg
                : { type: 'info', title: String(msg || '') };
        setFeedback(toast);
        const ms = toast.type === 'success' ? 8000 : toast.type === 'error' ? 3500 : 3000;
        feedbackTimer.current = setTimeout(() => setFeedback(null), ms);
    };

    useEffect(() => () => {
        if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
    }, []);

    // DEBUG: collapsible — only for local feel-test / allowlisted operator
    const [showDebug, setShowDebug] = useState(false);
    const showDevDrawer = canUseDebugTools();

    return (
        <div className="app-container">
            {IS_LOCAL_MODE && (
                <div className="local-feel-banner">
                    <strong>LOCAL FEEL-TEST</strong> — mock user <code>dev_user_local</code>.{' '}
                    <a href="?skipBootcamp=1">Skip bootcamp</a>
                </div>
            )}
            <header className="game-header atelier-lobby-game-header" style={{ display: viewMode === 'LOBBY' ? 'block' : 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h1>{GAME_NAME}</h1>
                    <button
                        type="button"
                        className="atelier-chip-btn"
                        onClick={() => setViewMode('GAME')}
                    >
                        Back to Hub
                    </button>
                </div>
            </header>

            {feedback && feedback.type !== 'success' && feedback.type !== 'error' && (
                <div className="status-panel dev-status">
                    <div className="msg-area">{feedback.title || feedback.body}</div>
                </div>
            )}

            {feedback?.type === 'success' && (
                <div className="game-toast game-toast--success" role="status">
                    <div className="game-toast-title">{feedback.title}</div>
                    {feedback.body && <div className="game-toast-body">{feedback.body}</div>}
                    {Array.isArray(feedback.rewards) && feedback.rewards.length > 0 && (
                        <ul className="game-toast-rewards">
                            {feedback.rewards.map((line) => (
                                <li key={line}>
                                    <span>{String(line).split(':')[0]}</span>
                                    <strong>{String(line).includes(':') ? String(line).slice(String(line).indexOf(':') + 1).trim() : ''}</strong>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
            {feedback?.type === 'error' && (
                <div className="game-toast game-toast--error" role="alert">
                    <div className="game-toast-title">{feedback.title}</div>
                    {feedback.body && <div className="game-toast-body">{feedback.body}</div>}
                </div>
            )}

            {viewMode === 'LOBBY' ? (
                <SquadLobby
                    initialView={lobbyInitialView}
                    onSelectSquad={(id, name) => {
                    if (id === 'public-workshop-0000') {
                        gameState.setMysteryTier('workshop');
                    } else if (id === 'public-syndicate-0000') {
                        // Public Atelier requires unlock; fall back to workshop if locked
                        if (gameState.atelierUnlocked) {
                            gameState.setMysteryTier('atelier');
                        } else {
                            gameState.setMysteryTier('workshop');
                            id = 'public-workshop-0000';
                            name = 'Public Workshop';
                            setFeedback({
                                type: 'info',
                                title: 'Atelier locked',
                                body: 'Opened Public Workshop — unlock Atelier by solving Workshop slices.',
                            });
                        }
                    }
                    gameState.setSquad(id, name);
                    setViewMode('GAME');
                    setLobbyInitialView('list');
                    fetchVault().then((res) => {
                        if (!res || !res.success) setFeedback('FAILED TO LOAD SQUAD VAULT');
                    });
                }} />
            ) : (
                <>
                    {showDevDrawer && (
                        <div className="dev-drawer">
                            <button
                                type="button"
                                className="dev-drawer-toggle"
                                onClick={() => setShowDebug((v) => !v)}
                            >
                                {showDebug ? '▾ Hide debug controls' : '▸ Dev controls'}
                            </button>
                            {showDebug && (
                                <div className="dev-drawer-body">
                                    <GameControls onFeedback={handleFeedback} />
                                    <button
                                        type="button"
                                        className="outline-btn"
                                        style={{ marginTop: 8 }}
                                        onClick={() => {
                                            setLobbyInitialView('list');
                                            setViewMode('LOBBY');
                                        }}
                                    >
                                        Syndicates
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {(gameState.isBootcampMode) ? (
                        <BootcampVault
                            onFeedback={handleFeedback}
                            onComplete={async (action) => {
                                localStorage.setItem(BOOTCAMP_STORAGE_KEY, 'true');
                                await completeBootcampProgress();
                                sessionStorage.setItem(
                                    'vq_post_bootcamp',
                                    action || 'lobby'
                                );
                                window.location.reload();
                            }}
                        />
                    ) : (
                        <MysteryHub
                            onFeedback={handleFeedback}
                            onOpenLobby={() => {
                                setLobbyInitialView('list');
                                setViewMode('LOBBY');
                            }}
                        />
                    )}
                </>
            )}

            {/* Modal Layer */}
            {selectedTile && (
                <TileDetailsModal
                    tile={selectedTile}
                    onClose={() => {
                        const isOwner = selectedTile.claimedBy === gameState.currentUser?.id;
                        if (isOwner) {
                            setIsMinimized(true);
                            gameState.clearSelection();
                        } else {
                            gameState.clearSelection();
                        }
                    }}
                    onFeedback={handleFeedback}
                />
            )}

            <footer className="app-footer-atelier">Voxel Quest · Magica Atelier · v0.1.0</footer>
        </div>
    );
};

export default VoxelGame;
